import React, { useState, useEffect, useCallback } from 'react';
import { Button } from 'react-bootstrap';
import { formatDateTime } from '../utils/formatDate';

// Autorizaciones: sales ask an admin to authorize editing or deleting a contribution or a despacho (backend
// utils/approvals.js). Admins see every request here and decide; sales see their own with the outcome. Shown in a
// modal opened from the header's user menu (header.js, which also shows the count). `onDecided(approved)` runs after
// every decision.
// A change to the requests (asked, decided): the header refreshes its badge. An approval carried out: the dashboards
// refresh their numbers.
export const APPROVALS_CHANGED = 'mm-approvals-changed';
export const APPROVAL_APPLIED = 'mm-approval-applied';
// What the badge counts: for an admin what waits for a decision, for sales their requests denied in the last 2 days.
const freshDenial = (r) => r.status === 'denied' && Date.now() - new Date(r.decidedAt).getTime() < 2 * 24 * 60 * 60 * 1000;
export const approvalsBadge = (data) => (data.canDecide ? data.pending.length : data.recent.filter(freshDenial).length);

const actionLabel = (r) => `${r.action.endsWith('.delete') ? 'Eliminar' : 'Editar'} ${r.entityLabel || ''}`;

const ApprovalsPanel = ({ onDecided }) => {
  const [data, setData] = useState({ canDecide: false, pending: [], recent: [] });
  const [busyId, setBusyId] = useState(null);
  const [denying, setDenying] = useState(null); // the request whose reason box is open
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [showRecent, setShowRecent] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/approvals');
      if (res.ok) setData(await res.json());
    } catch (err) {
      console.error('❌ Error fetching approvals:', err);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 60000);
    return () => clearInterval(timer);
  }, [load]);

  const decide = async (request, approve) => {
    setBusyId(request._id);
    setError('');
    try {
      const res = await fetch(`/api/approvals/${request._id}/decide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approve, reason: approve ? undefined : reason }),
      });
      const body = await res.json();
      if (!res.ok) setError(body.error || 'No se pudo decidir la solicitud.');
      setDenying(null);
      setReason('');
      await load();
      if (onDecided) onDecided(res.ok && approve);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const { canDecide, pending, recent } = data;
  // The requester sees a fresh denial without having to open the resolved list.
  const shownRecent = showRecent ? recent : canDecide ? [] : recent.filter(freshDenial);

  return (
        <div>
          {recent.length > 0 && (
            <div className="text-right">
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setShowRecent((v) => !v)}>
                {showRecent ? 'Ocultar resueltas' : `Ver resueltas (${recent.length})`}
              </button>
            </div>
          )}

          {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}

          {pending.length === 0 ? (
            <p className="text-muted mt-3 mb-0">No hay solicitudes pendientes.</p>
          ) : (
            <table className="table table-bordered mt-3 mb-0">
              <thead>
                <tr>
                  <th>Solicitud</th>
                  {canDecide && <th>Pedida por</th>}
                  <th>Fecha</th>
                  <th>{canDecide ? 'Decisión' : 'Estado'}</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((r) => (
                  <tr key={r._id}>
                    <td>
                      <strong>{actionLabel(r)}</strong>
                      <div className="text-muted small">{r.entityDetail}</div>
                      {r.summary && <div className="small">{r.summary}</div>}
                      {r.reason && <div className="small">Razón: {r.reason}</div>}
                      {canDecide && r.failure && <div className="small text-mm-red">{r.failure}</div>}
                    </td>
                    {canDecide && <td>{r.requestedBy}</td>}
                    <td>{formatDateTime(r.createdAt)}</td>
                    <td>
                      {!canDecide ? (
                        <span className="text-mm-yellow">Esperando a un administrador</span>
                      ) : denying === r._id ? (
                        <div>
                          <input
                            type="text"
                            className="form-control form-control-sm mb-2"
                            placeholder="Motivo (opcional)"
                            maxLength={300}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                          />
                          <div className="d-flex" style={{ gap: '8px' }}>
                            <Button size="sm" variant="danger" disabled={busyId === r._id} onClick={() => decide(r, false)}>
                              Denegar
                            </Button>
                            <Button size="sm" variant="secondary" onClick={() => { setDenying(null); setReason(''); }}>
                              Cancelar
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="d-flex" style={{ gap: '8px' }}>
                          <Button size="sm" variant="success" disabled={busyId === r._id} onClick={() => decide(r, true)}>
                            {busyId === r._id ? 'Aplicando...' : 'Aprobar'}
                          </Button>
                          <Button size="sm" variant="outline-danger" disabled={busyId === r._id} onClick={() => { setDenying(r._id); setReason(''); }}>
                            Denegar
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {shownRecent.length > 0 && (
            <table className="table table-bordered mt-3 mb-0">
              <thead>
                <tr>
                  <th>Solicitud</th>
                  {canDecide && <th>Pedida por</th>}
                  <th>Resultado</th>
                  <th>Decidida</th>
                </tr>
              </thead>
              <tbody>
                {shownRecent.map((r) => (
                  <tr key={r._id}>
                    <td>
                      {actionLabel(r)}
                      <div className="text-muted small">{r.entityDetail}</div>
                      {r.summary && <div className="small">{r.summary}</div>}
                      {r.reason && <div className="small">Razón: {r.reason}</div>}
                    </td>
                    {canDecide && <td>{r.requestedBy}</td>}
                    <td>
                      {r.status === 'approved' ? (
                        <span className="text-mm-green">Aprobada</span>
                      ) : (
                        <span className="text-mm-red">Denegada</span>
                      )}
                      {r.decisionReason && <div className="small">{r.decisionReason}</div>}
                    </td>
                    <td>
                      {formatDateTime(r.decidedAt)}
                      <div className="text-muted small">{r.decidedBy}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
  );
};

export default ApprovalsPanel;
